import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ColumnType } from "antd/es/table/interface";
import { App } from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  DeleteOutlined,
  EditOutlined,
  ShareAltOutlined,
  EllipsisOutlined,
} from "@ant-design/icons";

import QueryTable from "@/components_v2/Tables/QueryTable";
import { OrganizationRoles } from "@/shared/utils/constants";
import {
  MemberFiltersState,
  MemberTableProps,
} from "@/features/organizations/profile/types";
import MemberFilters from "@/features/organizations/profile/components/MemberFilters";
import { MembershipResponse } from "@/redux/memberships/types";
import {
  commonsApi,
  useDeleteMembershipByIdMutation,
  useLazyGetMembershipsQuery,
  useLazyImpersonateUserQuery,
} from "@/redux/service";
import DropdownMenu from "@/components_v2/DropdownMenu/DropdownMenu";
import BaseButton from "@/components_v2/Button/BaseButton";
import { useMemberPermissions } from "@/features/organizations/profile/hooks/useMemberPermissions";
import CancelConfirmationModal from "@/components_v2/Modal/CancelConfirmationModal";
import MembershipFormModal from "@/features/organizations/profile/components/MembershipFormModal";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import EyeOffIcon from "@/assets/icons/eye-off.svg?react";

import "../styles.css";
import { setAuthenticationState } from "@/redux/authentication/reducer";
import { LocalStorageName } from "@/redux/api";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import {
  BUTTON_SIZES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";

const MemberActionsCell = ({ member }: { member: MembershipResponse }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { notification } = App.useApp();

  const [impersonateUser] = useLazyImpersonateUserQuery();
  const currentUser = useSelector(selectCurrentUser);
  const [deleteMembershipById] = useDeleteMembershipByIdMutation();
  const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
  const [openEditMembershipModal, setOpenEditMembershipModal] = useState(false);
  const { canEdit, canDelete, canImpersonate } = useMemberPermissions(
    member,
    currentUser,
  );

  const handleImpersonateUser = (userId: number) => {
    impersonateUser(userId)
      .unwrap()
      .then((response) => {
        dispatch(commonsApi.util.resetApiState());
        dispatch(
          setAuthenticationState({
            isAuthenticated: true,
            user: response.user,
            isFirstLogin: false,
          }),
        );
        window.localStorage.setItem(
          LocalStorageName,
          JSON.stringify({
            accessExpiration: response.accessExpiration,
            refreshExpiration: response.refreshExpiration,
            user: response.user,
          }),
        );
        navigate("/");
      });
  };

  const handleDeleteMember = (memberId: number) => {
    deleteMembershipById(memberId)
      .unwrap()
      .then(() => {
        notification.success({
          message: t("organizations.labels.memberDeleted"),
        });
        setOpenConfirmDeleteModal(false);
      })
      .catch((errors) => {
        const error = errors?.data?.errors[0];
        if (
          [
            "delete_admin_user",
            "delete_own_membership",
            "delete_admin_user_as_app_admin",
          ].includes(error?.code)
        ) {
          notification.error({
            message: t(`organizations.labels.${error.code}`),
          });
        }
      });
  };

  return (
    <>
      <DropdownMenu
        content={
          <div>
            {canImpersonate && (
              <BaseTooltip
                title={
                  !member.user.isActive
                    ? t(
                        "organizations.labels.userIsInactiveAndCannotBeImpersonated",
                      )
                    : ""
                }
              >
                <BaseButton
                  className="dropdown-menu-button"
                  isFullWidth
                  size={BUTTON_SIZES.SMALL}
                  variant={BUTTON_VARIANTS.LINK}
                  disabled={!member.user.isActive}
                  leadingIcon={<ShareAltOutlined />}
                  onClick={() => handleImpersonateUser(member.user.id)}
                >
                  {t("organizations.labels.impersonate")}
                </BaseButton>
              </BaseTooltip>
            )}

            {canEdit && (
              <BaseButton
                className="dropdown-menu-button"
                isFullWidth
                variant="link"
                size="small"
                leadingIcon={<EditOutlined />}
                onClick={() => setOpenEditMembershipModal(true)}
              >
                {t("organizations.labels.edit")}
              </BaseButton>
            )}

            {canDelete && (
              <BaseButton
                className="dropdown-menu-button"
                isFullWidth
                variant="link"
                size="small"
                leadingIcon={<DeleteOutlined />}
                onClick={() => setOpenConfirmDeleteModal(true)}
              >
                {t("organizations.labels.delete")}
              </BaseButton>
            )}
          </div>
        }
        placement="bottom"
      >
        <BaseButton
          variant="link"
          size="small"
          leadingIcon={<EllipsisOutlined />}
        />
      </DropdownMenu>

      <MembershipFormModal
        open={openEditMembershipModal}
        setOpen={setOpenEditMembershipModal}
        defaultData={member}
      />
      <CancelConfirmationModal
        open={openConfirmDeleteModal}
        onCancel={() => setOpenConfirmDeleteModal(false)}
        onConfirm={() => handleDeleteMember(member.id)}
        title={t("organizations.labels.confirmMemberDelete")}
        confirmButtonText={t("organizations.labels.delete")}
        content={t("organizations.labels.confirmDeleteMember", {
          name: member.user.fullName,
        })}
      />
    </>
  );
};

const MemberTable = ({
  columnNames,
  filterNames,
  defaultFilters = {},
  additionalColumns,
  ...props
}: MemberTableProps<MembershipResponse>) => {
  const { t } = useTranslation();

  const currentUser = useSelector(selectCurrentUser);
  const [filters, setFilters] =
    useState<Partial<MemberFiltersState>>(defaultFilters);

  const columns: Record<
    string,
    ColumnType<MembershipResponse>
  > = useMemo(() => {
    const predefinedColumns: Record<string, ColumnType<MembershipResponse>> = {
      fullName: {
        title: t("organizations.labels.fullName"),
        key: "fullName",
        dataIndex: ["user", "fullName"],
        render: (fullName: string, member: MembershipResponse) => {
          return <Link to={`/users/${member.user.id}`}>{fullName}</Link>;
        },
      },
      email: {
        title: t("organizations.labels.email"),
        key: "email",
        dataIndex: ["user", "email"],
        render: (email: string) => {
          return (
            email || (
              <span className="flex items-center gap-100">
                <BaseSvgRenderer
                  component={EyeOffIcon}
                  width="16"
                  height="16"
                />
                {t("users.labels.emailHidden")}
              </span>
            )
          );
        },
      },
      role: {
        title: t("organizations.labels.role"),
        dataIndex: "role",
        render: (role: string) => {
          return OrganizationRoles.getItemByValue(role)?.label;
        },
      },
      actions: {
        title: t("organizations.labels.actions"),
        key: "actions",
        render: (member: MembershipResponse) =>
          member.user.id !== currentUser?.id ? (
            <MemberActionsCell member={member} />
          ) : null,
      },
    };
    return { ...predefinedColumns, ...(additionalColumns || {}) };
  }, [additionalColumns]);

  const mappedColumns = useMemo(
    () =>
      columnNames.map(
        (columnKey) => columns[columnKey] as ColumnType<MembershipResponse>,
      ),
    [columns, columnNames],
  );

  return (
    <div className="table-container-layout">
      <MemberFilters
        filterNames={filterNames}
        setFilters={setFilters}
        defaultFilters={defaultFilters}
      />
      <QueryTable<MembershipResponse>
        query={useLazyGetMembershipsQuery}
        filters={filters}
        initialSorting={{
          field: "name",
          order: "descend",
        }}
        columns={mappedColumns}
        {...props}
      />
    </div>
  );
};

export default MemberTable;
